export const IDENTITY_PROVIDER = 'idp-integ.federate.amazon.com';
export const FEDERATE_CLIENT_ID = 'content-connection';
export const FEDERATE_ID_TOKEN = 'federate_id_token';
export const FEDERATE_LOGIN_PATH = '/api/oauth2/v1/authorize';

export const APP_CONFIG = {
  identityProvider: IDENTITY_PROVIDER,
  identityEndpoint: `https://${IDENTITY_PROVIDER}${FEDERATE_LOGIN_PATH}`,
  federateIdToken: FEDERATE_ID_TOKEN,
  federateClientId: FEDERATE_CLIENT_ID,
};

// --- SCE-11405 Phase 1b: Federate -> Cognito Identity Pool -> SigV4 -----------------
// The portal SigV4-signs Connection Management API calls whenever a Cognito Identity Pool is
// configured for the active region + stage (see CONNECTION_AUTH_CONFIG / getIdentityPoolId).
// This is safe to ship before the API Gateway is flipped to AWS_IAM (Phase 2): while the API is
// authorizationType=NONE the extra signature is simply ignored. Region+stage combinations without
// a configured pool fall back to unsigned calls, so no flag is needed -- signing turns on per
// stage as each pool id is filled in.
import { Stage } from '../../constants/configurations';

// Cognito Identity Pools that broker Federate id_token -> temporary IAM credentials.
// `cognitoRegion` is where GetId/GetCredentialsForIdentity are called; `sigv4Region` is the
// region used to SigV4-sign execute-api requests (must match the API Gateway region).
// The China pools live in cn-north-1 (Beijing) because cn-northwest-1 (Ningxia) has no Cognito;
// their creds are still valid for the ZHY execute-api since they share the aws-cn account.
// Pool ids differ per stage and come from each FederateAuthStack CfnOutput
// (ContentConnection-<stage>-IdentityPoolId). Empty string = not yet deployed -> unsigned fallback.
export const CONNECTION_AUTH_CONFIG: Record<
  'global' | 'china',
  { cognitoRegion: string; sigv4Region: string; identityPoolIdByStage: Record<Stage, string> }
> = {
  global: {
    cognitoRegion: 'us-east-1',
    sigv4Region: 'us-east-1',
    // CONNECTION_MANAGEMENT_ENDPOINTS.global points every stage at the SAME prod us-east-1 API
    // (t8vb7h9mc8, acct 084515815455), so all four stages use the single prod-us-east-1 pool --
    // the pool must match the backend account each stage actually connects to.
    identityPoolIdByStage: {
      [Stage.DEV]: 'us-east-1:a1cc2e80-464f-46f1-a613-386b14482248',
      [Stage.BETA]: 'us-east-1:a1cc2e80-464f-46f1-a613-386b14482248',
      [Stage.GAMMA]: 'us-east-1:a1cc2e80-464f-46f1-a613-386b14482248',
      [Stage.PROD]: 'us-east-1:a1cc2e80-464f-46f1-a613-386b14482248',
    },
  },
  china: {
    cognitoRegion: 'cn-north-1', // Beijing -- Cognito is unavailable in cn-northwest-1
    sigv4Region: 'cn-northwest-1', // API Gateway region
    // CONNECTION_MANAGEMENT_ENDPOINTS.china points every stage at the SAME prod-cn API
    // (df5mb8sp36, acct 576300555274 -- verified the id exists only in prod-cn, NotFound in
    // beta-cn/gamma-cn), so all four stages use the single prod-cn pool -- the pool must match
    // the backend account each stage actually connects to. This mirrors the global config.
    identityPoolIdByStage: {
      [Stage.DEV]: 'cn-north-1:59366827-acc4-49db-a804-93910e63f3c7',
      [Stage.BETA]: 'cn-north-1:59366827-acc4-49db-a804-93910e63f3c7',
      [Stage.GAMMA]: 'cn-north-1:59366827-acc4-49db-a804-93910e63f3c7',
      [Stage.PROD]: 'cn-north-1:59366827-acc4-49db-a804-93910e63f3c7',
    },
  },
};

// Resolve the Cognito Identity Pool id for a region + stage; '' when none is configured yet.
export const getIdentityPoolId = (region: 'global' | 'china', stage: Stage): string =>
  CONNECTION_AUTH_CONFIG[region].identityPoolIdByStage[stage] ?? '';
